import { useTranslation } from "react-i18next";
import { useRef, useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ScrollReveal } from "./ScrollReveal";

/** Section-Anker der "App in Aktion"-Galerie (Navbar-Muster: scroll via getElementById). */
const SCREENSHOT_SECTION_ID = "app-in-action";
const SCREENSHOT_TRACK_ID = "screenshot-track";

export type ScreenshotSlideKey =
  | "dashboard"
  | "tasks"
  | "inspections"
  | "defects"
  | "checklists"
  | "pdf";

const screenshots: Array<{ key: ScreenshotSlideKey; src: string; labelKey: string }> = [
  { key: "dashboard", src: "/guide/dashboard.png", labelKey: "landing.screenshots.dashboard" },
  { key: "tasks", src: "/guide/tasks.png", labelKey: "landing.screenshots.tasks" },
  {
    key: "inspections",
    src: "/guide/inspections.png",
    labelKey: "landing.screenshots.inspections",
  },
  { key: "defects", src: "/guide/defects.png", labelKey: "landing.screenshots.defects" },
  { key: "checklists", src: "/guide/checklists.png", labelKey: "landing.screenshots.checklists" },
  { key: "pdf", src: "/guide/pdfProfiles.png", labelKey: "landing.screenshots.pdf" },
];

const SLIDE_GAP = 24; // gap-6

/** Breite eines Slides inkl. Gap — eine Quelle für Track-Scroll, Dots und Pfeile. */
function slideStride(track: HTMLElement): number {
  const cardWidth = track.querySelector("div")?.offsetWidth ?? 420;
  return cardWidth + SLIDE_GAP;
}

/**
 * Scrollt zur "App in Aktion"-Sektion und aktiviert den gewünschten Slide (TES-1072).
 * Gleiche Mechanik wie die Dot-Buttons der Galerie + scrollToSection der Navbar —
 * DOM-basiert, damit Geschwister-Komponenten (FeatureGrid) ohne Lifting ansteuern können.
 */
export function scrollToScreenshotSlide(key: ScreenshotSlideKey) {
  document.getElementById(SCREENSHOT_SECTION_ID)?.scrollIntoView({ behavior: "smooth" });

  const track = document.getElementById(SCREENSHOT_TRACK_ID);
  const index = screenshots.findIndex((s) => s.key === key);
  if (!track || index < 0) return;
  track.scrollTo({ left: index * slideStride(track), behavior: "smooth" });
}

function PerspectiveCard({ src, label, testId }: { src: string; label: string; testId: string }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [transform, setTransform] = useState("perspective(1000px) rotateY(0deg) rotateX(0deg)");

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTransform(`perspective(1000px) rotateY(${x * 10}deg) rotateX(${-y * 10}deg)`);
  };

  const handleMouseLeave = () => {
    setTransform("perspective(1000px) rotateY(0deg) rotateX(0deg)");
  };

  return (
    <div
      ref={cardRef}
      data-testid={testId}
      className="flex-shrink-0 w-[340px] sm:w-[420px] transition-transform duration-300 ease-out"
      style={{ transform }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div className="rounded-xl overflow-hidden border border-border/50 shadow-xl bg-card/50 backdrop-blur-sm">
        <img
          src={src}
          alt={label}
          className="w-full h-auto"
          width={420}
          height={236}
          loading="lazy"
          decoding="async"
        />
        <div className="p-3 text-center text-sm font-medium text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

export function ScreenshotShowcase() {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);

    // Calculate active index based on scroll position
    const index = Math.round(el.scrollLeft / slideStride(el));
    setActiveIndex(Math.min(index, screenshots.length - 1));
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState]);

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const stride = slideStride(el);
    el.scrollBy({ left: direction === "left" ? -stride : stride, behavior: "smooth" });
  };

  return (
    <section id={SCREENSHOT_SECTION_ID} className="py-24 bg-muted/30 overflow-hidden">
      <div className="container mx-auto px-6 mb-12">
        <ScrollReveal>
          <div className="text-center space-y-4">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
              {t("landing.screenshots.title")}
            </h2>
            <p className="text-lg text-muted-foreground">{t("landing.screenshots.subtitle")}</p>
          </div>
        </ScrollReveal>
      </div>

      <ScrollReveal>
        <div className="relative group">
          {/* Left arrow */}
          {canScrollLeft && (
            <button
              onClick={() => scroll("left")}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-background/80 backdrop-blur-sm border border-border/50 rounded-full p-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-background text-foreground"
              aria-label="Scroll left"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}

          {/* Right arrow */}
          {canScrollRight && (
            <button
              onClick={() => scroll("right")}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-background/80 backdrop-blur-sm border border-border/50 rounded-full p-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-background text-foreground"
              aria-label="Scroll right"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}

          <div
            ref={scrollRef}
            id={SCREENSHOT_TRACK_ID}
            className="flex gap-6 overflow-x-auto pb-6 px-6 snap-x snap-mandatory"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "hsl(var(--muted-foreground) / 0.3) transparent",
            }}
          >
            {screenshots.map((s) => (
              <PerspectiveCard
                key={s.src}
                src={s.src}
                label={t(s.labelKey)}
                testId={`screenshot-slide-${s.key}`}
              />
            ))}
          </div>

          {/* Dot indicator */}
          <div className="flex justify-center gap-2 mt-2">
            {screenshots.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  const el = scrollRef.current;
                  if (!el) return;
                  el.scrollTo({ left: i * slideStride(el), behavior: "smooth" });
                }}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === activeIndex ? "w-6 bg-primary" : "w-2 bg-muted-foreground/30"
                }`}
                aria-label={`Screenshot ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </ScrollReveal>
    </section>
  );
}
